"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  BedDouble,
  Bath,
  Ruler,
  Building2,
  Waves,
  Users,
} from "lucide-react";
import { SITE } from "@/lib/constants";

const STATS = [
  { icon: BedDouble, value: SITE.bedrooms, label: "Bedrooms", suffix: "" },
  { icon: Bath, value: SITE.bathrooms, label: "Bathrooms", suffix: "" },
  { icon: Ruler, value: SITE.sqft, label: "Sq Ft", suffix: "", formatted: true },
  { icon: Building2, value: SITE.floors, label: "Floors", suffix: "" },
  { icon: Waves, value: 1, label: "Pool", suffix: "" },
  { icon: Users, value: SITE.maxGuests, label: "Max Guests", suffix: "" },
];

function AnimatedCounter({ value, formatted }: { value: number; formatted?: boolean }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    const duration = 1200;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {formatted ? count.toLocaleString("en-IN") : count}
    </span>
  );
}

export function StatsBar() {
  return (
    <section className="relative border-y border-primary/20 bg-card/50">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
      <div className="relative mx-auto max-w-7xl overflow-x-auto px-4 py-8 md:px-8">
        <div className="flex min-w-max items-center justify-between gap-8 md:gap-12">
          {STATS.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex items-center gap-3"
            >
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                <stat.icon className="size-5 shrink-0 text-primary" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground md:text-2xl">
                  <AnimatedCounter value={stat.value} formatted={stat.formatted} />
                  {stat.suffix}
                </p>
                <p className="text-xs text-muted-foreground md:text-sm">
                  {stat.label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
